import { IMatchDetailDTO } from "../../models/IMatch";
import { formatDateLocal } from "../../utils/dateFormat";
import { MatchConfig, GroupDateAssignment, VenueAssignment, VenueRange } from "./types";

// --- Shared helpers ---

export const hasTeamConflict = (m1: IMatchDetailDTO, m2: IMatchDetailDTO): boolean => {
  if (!m1.homeTeamId || !m1.awayTeamId || !m2.homeTeamId || !m2.awayTeamId) return false;
  return (
    m1.homeTeamId === m2.homeTeamId ||
    m1.homeTeamId === m2.awayTeamId ||
    m1.awayTeamId === m2.homeTeamId ||
    m1.awayTeamId === m2.awayTeamId
  );
};

export const hasTeamConflictAtTime = (
  match: IMatchDetailDTO,
  time: Date,
  scheduledMatches: IMatchDetailDTO[]
): boolean => {
  if (!match.homeTeamId || !match.awayTeamId) return false;

  const timeValue = time.getTime();
  return scheduledMatches.some((scheduledMatch) => {
    if (!scheduledMatch.startTime) return false;
    const scheduledTime = new Date(scheduledMatch.startTime).getTime();
    if (Math.abs(scheduledTime - timeValue) < 1000) {
      return hasTeamConflict(match, scheduledMatch);
    }
    return false;
  });
};

export const getCourtNumber = (venueName: string): number | null => {
  const match = venueName.match(/Court\s+(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
};

// --- Validation ---

export const validateMatchConfigurations = (params: {
  matches: IMatchDetailDTO[];
  configDates: string[];
  formatName: string;
  groupDateAssignments: { [groupId: string]: GroupDateAssignment };
  matchConfigs: { [key: string]: MatchConfig };
  selectedType: string;
}): string | null => {
  const { matches, configDates, formatName, groupDateAssignments, matchConfigs, selectedType } = params;

  if (formatName === "Group") {
    const hasGroupSpecificDates = Object.keys(groupDateAssignments).some(
      (groupId) => groupDateAssignments[groupId].dates.length > 0
    );

    if (hasGroupSpecificDates) {
      const groupsWithMatches = new Set(
        matches.map((match) => match.group).filter((group): group is string => Boolean(group))
      );
      const groupsWithDates = new Set(
        Object.keys(groupDateAssignments).filter((groupId) => groupDateAssignments[groupId].dates.length > 0)
      );

      const groupsWithoutDates = Array.from(groupsWithMatches).filter((groupId) => !groupsWithDates.has(groupId));

      if (groupsWithoutDates.length > 0) {
        return `Groups ${groupsWithoutDates.join(
          ", "
        )} have matches but no dates assigned. Please assign dates to all groups or remove group-specific date assignments.`;
      }

      for (const [groupId, groupAssignment] of Object.entries(groupDateAssignments)) {
        if (groupAssignment.dates.length === 0) continue;

        const groupMatches = matches.filter((match) => match.group === groupId);
        let totalGroupSlots = 0;

        for (const date of groupAssignment.dates) {
          const dateStr = formatDateLocal(date);
          const config = groupAssignment.configs[dateStr];
          if (config && config.matchesPerDay) {
            totalGroupSlots += config.matchesPerDay;
          }
        }

        if (totalGroupSlots < groupMatches.length) {
          return `Group ${groupId} has ${groupMatches.length} matches but only ${totalGroupSlots} slots configured. Please add more dates or increase matches per day for this group.`;
        }
      }

      return null;
    }
  }

  if (configDates.length === 0 && selectedType === "manual") {
    return "Please select at least one match date before proceeding.";
  }

  let totalSchedulableMatches = 0;
  for (const dateStr of configDates) {
    const config = matchConfigs[dateStr];
    if (config && config.matchesPerDay) {
      totalSchedulableMatches += config.matchesPerDay;
    }
  }

  if (configDates.length > 0 && totalSchedulableMatches !== matches.length) {
    return `Total matches per day across all dates (${totalSchedulableMatches}) does not match the total number of matches (${matches.length}). Please adjust the matches per day for each date so they add up to ${matches.length}.`;
  }

  return null;
};

// --- Group matches with specific dates ---

export const scheduleGroupMatchesWithSpecificDates = (params: {
  result: IMatchDetailDTO[];
  groupDateAssignments: { [groupId: string]: GroupDateAssignment };
  matchConfigs: { [key: string]: MatchConfig };
  groupVenueAssignments: { [groupId: string]: VenueAssignment };
  scheduleStandard: (matches: IMatchDetailDTO[], dates: string[]) => void;
}): void => {
  const { result, groupDateAssignments, matchConfigs, groupVenueAssignments, scheduleStandard } = params;

  const matchesByGroup: { [groupId: string]: IMatchDetailDTO[] } = {};
  const unassignedMatches: IMatchDetailDTO[] = [];

  result.forEach((match) => {
    const groupId = match.group;
    if (groupId && groupDateAssignments[groupId]) {
      if (!matchesByGroup[groupId]) matchesByGroup[groupId] = [];
      matchesByGroup[groupId].push(match);
    } else {
      unassignedMatches.push(match);
    }
  });

  for (const [groupId, groupMatches] of Object.entries(matchesByGroup)) {
    const groupDates = groupDateAssignments[groupId].dates;
    const groupConfigs = matchConfigs;

    let matchIndex = 0;
    for (const date of groupDates) {
      const dateStr = formatDateLocal(date);
      const config = groupConfigs[dateStr];
      if (!config) continue;

      const { interval, startTime, matchesPerDay, bestOf } = config;
      const baseStartTime = new Date(startTime);

      for (let i = 0; i < matchesPerDay && matchIndex < groupMatches.length; i++) {
        const match = groupMatches[matchIndex];
        const matchStartTime = new Date(baseStartTime);
        matchStartTime.setMinutes(matchStartTime.getMinutes() + i * interval);

        match.startTime = matchStartTime.toISOString();
        match.bestOf = bestOf || 1;

        if (groupVenueAssignments[groupId] && groupVenueAssignments[groupId].venueNames.length > 0) {
          const venueIndex = i % groupVenueAssignments[groupId].venueNames.length;
          match.venue = groupVenueAssignments[groupId].venueNames[venueIndex];
        } else {
          match.venue = `Court ${(i % 4) + 1}`;
        }

        matchIndex++;
      }
    }

    if (matchIndex < groupMatches.length && groupDates.length > 0) {
      const lastDate = groupDates[groupDates.length - 1];
      const lastDateStr = formatDateLocal(lastDate);
      const lastConfig = groupConfigs[lastDateStr];

      if (lastConfig) {
        const { interval, startTime, bestOf } = lastConfig;
        const baseStartTime = new Date(startTime);

        for (let i = matchIndex; i < groupMatches.length; i++) {
          const match = groupMatches[i];
          const matchStartTime = new Date(baseStartTime);
          matchStartTime.setMinutes(matchStartTime.getMinutes() + (i - matchIndex) * interval);

          match.startTime = matchStartTime.toISOString();
          match.bestOf = bestOf || 1;

          if (groupVenueAssignments[groupId] && groupVenueAssignments[groupId].venueNames.length > 0) {
            const venueIndex = (i - matchIndex) % groupVenueAssignments[groupId].venueNames.length;
            match.venue = groupVenueAssignments[groupId].venueNames[venueIndex];
          } else {
            match.venue = `Court ${((i - matchIndex) % 4) + 1}`;
          }
        }
      }
    }
  }

  if (unassignedMatches.length > 0) {
    scheduleStandard(unassignedMatches, Object.keys(matchConfigs).sort());
  }
};

// --- Group matches with venues (Bug 2 fix applied here) ---

export const scheduleGroupMatchesWithVenues = (params: {
  result: IMatchDetailDTO[];
  configDates: string[];
  matchConfigs: { [key: string]: MatchConfig };
  groupVenueAssignments: { [groupId: string]: VenueAssignment };
  venueRange: VenueRange;
}): void => {
  const { result, configDates, matchConfigs, groupVenueAssignments, venueRange } = params;

  const allAvailableVenues: string[] = [];
  const venueToGroupsMap: { [venueName: string]: string[] } = {};

  Object.entries(groupVenueAssignments).forEach(([groupId, assignment]) => {
    if (assignment.venueNames && assignment.venueNames.length > 0) {
      assignment.venueNames.forEach((venueName) => {
        if (!allAvailableVenues.includes(venueName)) {
          allAvailableVenues.push(venueName);
        }
        if (!venueToGroupsMap[venueName]) venueToGroupsMap[venueName] = [];
        venueToGroupsMap[venueName].push(groupId);
      });
    }
  });

  if (allAvailableVenues.length === 0) {
    for (let i = venueRange.start; i <= venueRange.end; i++) {
      allAvailableVenues.push(`Court ${i}`);
    }
  }

  const validVenues = allAvailableVenues.filter((venueName) => {
    const match = venueName.match(/Court (\d+)/);
    if (match) {
      const courtNum = parseInt(match[1]);
      return courtNum >= venueRange.start && courtNum <= venueRange.end;
    }
    return true;
  });

  if (validVenues.length < allAvailableVenues.length) {
    allAvailableVenues.length = 0;
    allAvailableVenues.push(...validVenues);
  }

  // Organize matches by round
  const matchesByRound: { [round: string]: IMatchDetailDTO[] } = {};
  result.forEach((match) => {
    const round = match.round || "Unknown";
    if (!matchesByRound[round]) matchesByRound[round] = [];
    matchesByRound[round].push(match);
  });

  const roundOrder = ["Round 1", "Round 2", "Round 3", "Round 4", "Round 5", "Round 6", "Round 7", "Round 8"];
  const rounds = Object.keys(matchesByRound).sort((a, b) => {
    const aIndex = roundOrder.indexOf(a);
    const bIndex = roundOrder.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  const globalVenueTimings: { [venueName: string]: { dateIndex: number; slotIndex: number } } = {};
  allAvailableVenues.forEach((venue) => {
    globalVenueTimings[venue] = { dateIndex: 0, slotIndex: 0 };
  });

  // Track total matches scheduled per date (across ALL venues)
  const matchesOnDate: { [dateIndex: number]: number } = {};

  const advanceAllVenuesToNextDate = (fullDateIndex: number) => {
    allAvailableVenues.forEach((venue) => {
      if (globalVenueTimings[venue].dateIndex === fullDateIndex) {
        globalVenueTimings[venue].dateIndex++;
        globalVenueTimings[venue].slotIndex = 0;
      }
    });
  };

  for (const round of rounds) {
    const roundMatches = [...matchesByRound[round]];

    const matchesByGroup: { [groupId: string]: IMatchDetailDTO[] } = {};
    roundMatches.forEach((match) => {
      const groupId = match.group || "NoGroup";
      if (!matchesByGroup[groupId]) matchesByGroup[groupId] = [];
      matchesByGroup[groupId].push(match);
    });

    const matchesToSchedule: Array<{
      match: IMatchDetailDTO;
      groupId: string;
      allowedVenues: string[];
    }> = [];

    for (const [groupId, groupMatches] of Object.entries(matchesByGroup)) {
      let allowedVenues: string[] = [];

      if (groupId !== "NoGroup" && groupVenueAssignments[groupId]) {
        const assignment = groupVenueAssignments[groupId];
        allowedVenues =
          assignment.venueNames && assignment.venueNames.length > 0
            ? [...assignment.venueNames]
            : [...allAvailableVenues];
      } else {
        allowedVenues = [...allAvailableVenues];
      }

      groupMatches.forEach((match) => {
        matchesToSchedule.push({ match, groupId, allowedVenues });
      });
    }

    let safetyCounter = 0;
    const maxAttempts = matchesToSchedule.length * allAvailableVenues.length * 10;

    while (matchesToSchedule.length > 0 && safetyCounter < maxAttempts) {
      safetyCounter++;

      // BUG 2 FIX: Only consider venues that at least one remaining match can use
      const neededVenues = new Set<string>();
      matchesToSchedule.forEach(({ allowedVenues }) => {
        allowedVenues.forEach((v) => neededVenues.add(v));
      });

      let earliestVenue = "";
      let earliestTime = Infinity;
      let earliestDateIndex = Infinity;
      let earliestSlotIndex = Infinity;

      for (const venue of allAvailableVenues) {
        if (!neededVenues.has(venue)) continue; // Skip venues no remaining match needs

        const timing = globalVenueTimings[venue];

        while (timing.dateIndex >= configDates.length) {
          const lastDate = configDates.length > 0 ? new Date(configDates[configDates.length - 1]) : new Date();
          lastDate.setDate(lastDate.getDate() + 1);

          const newDateStr = formatDateLocal(lastDate);
          configDates.push(newDateStr);

          const emergencyStartTime = new Date(lastDate);
          emergencyStartTime.setHours(19, 0, 0, 0);

          matchConfigs[newDateStr] = {
            matchesPerDay: 50,
            interval: 30,
            startTime: emergencyStartTime,
            venueCount: allAvailableVenues.length,
            bestOf: 1,
          };
        }

        const dateStr = configDates[timing.dateIndex];
        const config = matchConfigs[dateStr];
        if (!config) continue;

        const timeInMinutes = timing.dateIndex * 10000 + timing.slotIndex * config.interval;

        if (timeInMinutes < earliestTime) {
          earliestTime = timeInMinutes;
          earliestVenue = venue;
          earliestDateIndex = timing.dateIndex;
          earliestSlotIndex = timing.slotIndex;
        }
      }

      if (!earliestVenue) break;

      // Check if this date has reached its total matches cap
      const dateStr = configDates[earliestDateIndex];
      const config = matchConfigs[dateStr];
      if ((matchesOnDate[earliestDateIndex] || 0) >= config.matchesPerDay) {
        // This date is full — advance all venues still on this date to the next one
        advanceAllVenuesToNextDate(earliestDateIndex);
        continue;
      }

      const baseStartTime = new Date(config.startTime);
      const slotTime = new Date(baseStartTime.getTime() + earliestSlotIndex * config.interval * 60_000);

      let matchScheduled = false;

      for (let i = 0; i < matchesToSchedule.length; i++) {
        const { match, allowedVenues } = matchesToSchedule[i];

        if (!allowedVenues.includes(earliestVenue)) continue;

        const matchesAtThisTime = result.filter((m) => {
          if (!m.startTime) return false;
          const mTime = new Date(m.startTime).getTime();
          return Math.abs(mTime - slotTime.getTime()) < 1000;
        });

        const hasConflict = matchesAtThisTime.some((m) => hasTeamConflict(m, match));

        if (!hasConflict) {
          if (!allAvailableVenues.includes(earliestVenue)) {
            globalVenueTimings[earliestVenue].slotIndex++;
            break;
          }

          match.startTime = slotTime.toISOString();
          match.venue = earliestVenue;
          match.bestOf = config.bestOf || 1;

          matchesToSchedule.splice(i, 1);
          matchScheduled = true;

          // Track per-day total
          matchesOnDate[earliestDateIndex] = (matchesOnDate[earliestDateIndex] || 0) + 1;

          globalVenueTimings[earliestVenue].slotIndex++;

          // If this date is now full, advance all venues on it to the next date
          if (matchesOnDate[earliestDateIndex] >= config.matchesPerDay) {
            advanceAllVenuesToNextDate(earliestDateIndex);
          }

          break;
        }
      }

      if (!matchScheduled) {
        globalVenueTimings[earliestVenue].slotIndex++;
        const maxSlotsPerDay = Math.floor((24 * 60) / config.interval);

        if (globalVenueTimings[earliestVenue].slotIndex >= maxSlotsPerDay) {
          globalVenueTimings[earliestVenue].dateIndex++;
          globalVenueTimings[earliestVenue].slotIndex = 0;
        }
      }
    }
  }

  const unscheduledMatches = result.filter((m) => !m.startTime || !m.venue);
  if (unscheduledMatches.length > 0) {
    console.error(`${unscheduledMatches.length} matches still unscheduled!`);
  }
};

// --- Knockout matches with venues ---

export const scheduleKnockoutMatchesWithVenues = (params: {
  result: IMatchDetailDTO[];
  configDates: string[];
  matchConfigs: { [key: string]: MatchConfig };
  knockoutVenueAssignments: { [roundName: string]: VenueAssignment };
}): void => {
  const { result, configDates, matchConfigs, knockoutVenueAssignments } = params;

  let remainingMatches = [...result];
  const unassignedMatches: IMatchDetailDTO[] = [];

  for (let dateIndex = 0; dateIndex < configDates.length && remainingMatches.length > 0; dateIndex++) {
    const dateStr = configDates[dateIndex];
    const config = matchConfigs[dateStr];
    if (!config) continue;
    const { interval, startTime, matchesPerDay, bestOf } = config;
    const baseStartTime = new Date(startTime);

    let matchesScheduledToday = 0;
    const matchesForThisDate: IMatchDetailDTO[] = [];

    const matchesByRound: { [roundName: string]: IMatchDetailDTO[] } = {};
    remainingMatches.forEach((m) => {
      if (m.round) {
        const va = knockoutVenueAssignments[m.round];
        if (va && va.venueIds && va.venueIds.length > 0) {
          (matchesByRound[m.round] ||= []).push(m);
        } else {
          unassignedMatches.push(m);
        }
      }
    });

    const venueTimings: { [venueName: string]: number } = {};

    const roundOrder = ["Round of 32", "Round of 16", "Quarterfinals", "Semifinals", "Third Place", "Final"];
    const sortedRounds = Object.keys(matchesByRound).sort((a, b) => {
      const hasSharedVenues = () => {
        const thirdPlaceVenues = knockoutVenueAssignments["Third Place"]?.venueNames || [];
        const finalVenues = knockoutVenueAssignments["Final"]?.venueNames || [];
        return thirdPlaceVenues.filter((venue) => finalVenues.includes(venue)).length > 0;
      };

      if (hasSharedVenues() && a === "Third Place" && b === "Final") return -1;
      if (hasSharedVenues() && a === "Final" && b === "Third Place") return 1;
      return roundOrder.indexOf(a) - roundOrder.indexOf(b);
    });

    for (const roundName of sortedRounds) {
      const roundMatches = matchesByRound[roundName];
      if (!roundMatches || roundMatches.length === 0) continue;
      if (matchesScheduledToday >= matchesPerDay) break;

      const va = knockoutVenueAssignments[roundName];
      const venueIds = va.venueIds || [];
      const venueNames = va.venueNames || [];
      const venueCount = Math.max(1, venueIds.length);

      const matchQueue = [...roundMatches];
      const scheduledInThisRound = new Set<string>();

      while (matchQueue.length > 0 && matchesScheduledToday < matchesPerDay) {
        const available = matchQueue.filter((m) => !scheduledInThisRound.has(m.id));
        if (available.length === 0) break;

        const batchSize = Math.min(available.length, venueCount);
        const batch = available.slice(0, batchSize);
        if (batch.length === 0) break;

        for (let i = 0; i < batch.length && matchesScheduledToday < matchesPerDay; i++) {
          const match = batch[i];
          const hasTeams = match.homeTeamId && match.awayTeamId;

          let scheduled = false;
          let attempts = 0;
          const maxAttempts = venueCount * matchesPerDay;

          while (!scheduled && attempts < maxAttempts) {
            const venueIndex = (i + attempts) % venueCount;
            const assignedVenue =
              (venueNames[venueIndex] || venueIds[venueIndex]) ?? venueIds[venueIndex] ?? "Default Venue";

            if (!venueTimings[assignedVenue]) venueTimings[assignedVenue] = 0;

            const slotTime = new Date(baseStartTime.getTime() + venueTimings[assignedVenue] * interval * 60_000);

            const hasConflictAtTime = !hasTeams
              ? false
              : matchesForThisDate.some((scheduledMatch) => {
                  if (!scheduledMatch.startTime) return false;
                  const scheduledTime = new Date(scheduledMatch.startTime).getTime();
                  if (Math.abs(scheduledTime - slotTime.getTime()) < 1000) {
                    return hasTeamConflict(match, scheduledMatch);
                  }
                  return false;
                });

            if (!hasConflictAtTime) {
              match.startTime = slotTime.toISOString();
              match.venue = assignedVenue;
              match.bestOf = bestOf;

              matchesForThisDate.push(match);
              scheduledInThisRound.add(match.id);
              matchesScheduledToday++;
              scheduled = true;
              venueTimings[assignedVenue]++;
            } else {
              venueTimings[assignedVenue]++;
              attempts++;
            }
          }

          if (!scheduled) {
            scheduledInThisRound.add(match.id);
          }
        }
      }

      const scheduledIds = new Set(matchesForThisDate.map((m) => m.id));
      matchesByRound[roundName] = roundMatches.filter((m) => !scheduledIds.has(m.id));
    }

    remainingMatches = remainingMatches.filter((m) => !matchesForThisDate.includes(m));
  }

  if (unassignedMatches.length > 0) {
    for (let dateIndex = 0; dateIndex < configDates.length && unassignedMatches.length > 0; dateIndex++) {
      const dateStr = configDates[dateIndex];
      const config = matchConfigs[dateStr];
      if (!config) continue;
      const { interval, startTime, matchesPerDay, bestOf } = config;
      const baseStartTime = new Date(startTime);

      const existingMatchesCount = result.filter(
        (m) => m.startTime && new Date(m.startTime).toDateString() === baseStartTime.toDateString()
      ).length;

      const availableSlots = Math.max(0, matchesPerDay - existingMatchesCount);
      if (availableSlots <= 0) continue;

      let currentTime = new Date(baseStartTime.getTime());
      const toSchedule = Math.min(availableSlots, unassignedMatches.length);
      for (let i = 0; i < toSchedule; i++) {
        const match = unassignedMatches[i];
        match.startTime = currentTime.toISOString();
        match.venue = "Default Venue";
        match.bestOf = bestOf;
        currentTime.setMinutes(currentTime.getMinutes() + interval);
      }
      unassignedMatches.splice(0, toSchedule);
    }
  }
};

// --- League matches with venues ---

export const scheduleLeagueMatchesWithVenues = (params: {
  result: IMatchDetailDTO[];
  configDates: string[];
  matchConfigs: { [key: string]: MatchConfig };
  leagueVenueAssignments: VenueAssignment;
  scheduleStandard: (matches: IMatchDetailDTO[], dates: string[]) => void;
}): void => {
  const { result, configDates, matchConfigs, leagueVenueAssignments, scheduleStandard } = params;

  let remainingMatches = [...result];
  const venueIds = leagueVenueAssignments.venueIds || [];
  const venueNames = leagueVenueAssignments.venueNames || [];
  const venueCount = Math.max(1, venueIds.length);

  if (venueCount === 0) {
    scheduleStandard(result, configDates);
    return;
  }

  const venueTimings: { [venueName: string]: number } = {};
  venueNames.forEach((venueName) => {
    venueTimings[venueName] = 0;
  });

  for (let dateIndex = 0; dateIndex < configDates.length && remainingMatches.length > 0; dateIndex++) {
    const dateStr = configDates[dateIndex];
    const config = matchConfigs[dateStr];
    if (!config) continue;
    const { interval, startTime, matchesPerDay, bestOf } = config;
    const baseStartTime = new Date(startTime);

    // Reset venue timings for each new date so slot times start from this date's startTime
    venueNames.forEach((venueName) => {
      venueTimings[venueName] = 0;
    });

    let matchesScheduledToday = 0;
    const matchesForThisDate: IMatchDetailDTO[] = [];

    while (remainingMatches.length > 0 && matchesScheduledToday < matchesPerDay) {
      const match = remainingMatches[0];
      const hasTeams = match.homeTeamId && match.awayTeamId;

      let scheduled = false;
      let attempts = 0;
      const maxAttempts = venueNames.length * matchesPerDay;

      while (!scheduled && attempts < maxAttempts) {
        let earliestVenue = "";
        let earliestTime = Infinity;
        let earliestTiming = Infinity;

        for (const venueName of venueNames) {
          const timing = venueTimings[venueName];
          const slotTime = new Date(baseStartTime.getTime() + timing * interval * 60_000);
          const timeValue = slotTime.getTime();

          if (timeValue < earliestTime) {
            earliestTime = timeValue;
            earliestVenue = venueName;
            earliestTiming = timing;
          }
        }

        if (!earliestVenue) break;

        const slotTime = new Date(baseStartTime.getTime() + earliestTiming * interval * 60_000);
        const hasConflict = hasTeams ? hasTeamConflictAtTime(match, slotTime, matchesForThisDate) : false;

        if (!hasConflict) {
          match.startTime = slotTime.toISOString();
          match.venue = earliestVenue;
          match.bestOf = bestOf;

          matchesForThisDate.push(match);
          remainingMatches.shift();
          matchesScheduledToday++;
          scheduled = true;
          venueTimings[earliestVenue]++;
        } else {
          venueTimings[earliestVenue]++;
          attempts++;
        }
      }

      if (!scheduled) {
        remainingMatches.shift();
        break;
      }
    }
  }
};

// --- Group matches standard logic (no venue assignments) ---

export const scheduleGroupMatchesStandard = (params: {
  result: IMatchDetailDTO[];
  configDates: string[];
  matchConfigs: { [key: string]: MatchConfig };
}): void => {
  const { result, configDates, matchConfigs } = params;

  const matchesByGroup = result.reduce((acc, match) => {
    const group = match.group || "Unknown";
    if (!acc[group]) acc[group] = [];
    acc[group].push(match);
    return acc;
  }, {} as Record<string, IMatchDetailDTO[]>);

  let matchIndex = 0;
  let dateIndex = 0;

  while (matchIndex < result.length && dateIndex < configDates.length) {
    const dateStr = configDates[dateIndex];
    const config = matchConfigs[dateStr];
    const { matchesPerDay, interval, startTime, venueCount = 2, bestOf } = config;

    const venues = Array.from({ length: venueCount }, (_, i) => `Court ${i + 1}`);
    const venueTimings: { [venue: string]: number } = {};
    venues.forEach((venue) => {
      venueTimings[venue] = 0;
    });

    let matchesScheduledToday = 0;
    const baseStartTime = new Date(startTime);
    const matchesScheduledThisDate: IMatchDetailDTO[] = [];

    for (const [groupId, groupMatches] of Object.entries(matchesByGroup)) {
      if (!groupMatches || groupMatches.length === 0) continue;
      if (matchesScheduledToday >= matchesPerDay) break;

      let scheduledCount = 0;
      for (let i = 0; i < groupMatches.length && matchesScheduledToday < matchesPerDay; i++) {
        const match = groupMatches[i];
        const hasTeams = match.homeTeamId && match.awayTeamId;

        let scheduled = false;
        let attempts = 0;
        const maxAttempts = venueCount * matchesPerDay;

        while (!scheduled && attempts < maxAttempts) {
          let earliestVenue = venues[0];
          let earliestTime = venueTimings[venues[0]];

          for (const venue of venues) {
            if (venueTimings[venue] < earliestTime) {
              earliestTime = venueTimings[venue];
              earliestVenue = venue;
            }
          }

          const slotTime = new Date(baseStartTime.getTime() + venueTimings[earliestVenue] * interval * 60_000);
          const hasConflict = hasTeams ? hasTeamConflictAtTime(match, slotTime, matchesScheduledThisDate) : false;

          if (!hasConflict) {
            match.startTime = slotTime.toISOString();
            match.venue = earliestVenue;
            match.bestOf = bestOf;

            matchesScheduledThisDate.push(match);
            venueTimings[earliestVenue]++;
            matchIndex++;
            matchesScheduledToday++;
            scheduledCount++;
            scheduled = true;
          } else {
            venueTimings[earliestVenue]++;
            attempts++;
          }
        }

        if (!scheduled) break;
      }

      matchesByGroup[groupId] = groupMatches.slice(scheduledCount);
      if (matchesScheduledToday >= matchesPerDay) break;
    }

    dateIndex++;
  }
};

// --- Standard logic (knockout/league without venues) ---

export const scheduleMatchesStandard = (params: {
  result: IMatchDetailDTO[];
  configDates: string[];
  matchConfigs: { [key: string]: MatchConfig };
}): void => {
  const { result, configDates, matchConfigs } = params;

  const matchesByRound = result.reduce((acc, match) => {
    const round = match.round || "Unknown";
    if (!acc[round]) acc[round] = [];
    acc[round].push(match);
    return acc;
  }, {} as Record<string, IMatchDetailDTO[]>);

  const roundKeys = Object.keys(matchesByRound).sort((a, b) => {
    // "Round N" sorts numerically for ANY N (Americano/Mexicano and league rounds can
    // exceed 8); named knockout rounds keep their fixed order after the numbered ones.
    const numbered = /^Round (\d+)$/;
    const na = numbered.exec(a);
    const nb = numbered.exec(b);
    if (na && nb) return parseInt(na[1], 10) - parseInt(nb[1], 10);
    if (na) return -1;
    if (nb) return 1;
    const order = ["Round of 32", "Round of 16", "Quarterfinals", "Semifinals", "Third Place", "Final"];
    return order.indexOf(a) - order.indexOf(b);
  });

  let matchIndex = 0;
  let dateIndex = 0;

  while (matchIndex < result.length && dateIndex < configDates.length) {
    const dateStr = configDates[dateIndex];
    const config = matchConfigs[dateStr];
    const { matchesPerDay, interval, startTime, venueCount = 2, bestOf } = config;

    const venues = Array.from({ length: venueCount }, (_, i) => `Court ${i + 1}`);
    const venueTimings: { [venue: string]: number } = {};
    venues.forEach((venue) => {
      venueTimings[venue] = 0;
    });

    let matchesScheduledToday = 0;
    const baseStartTime = new Date(startTime);
    const matchesScheduledThisDate: IMatchDetailDTO[] = [];

    for (const round of roundKeys) {
      const roundMatches = matchesByRound[round];
      if (!roundMatches || roundMatches.length === 0) continue;

      let scheduledCount = 0;
      for (let i = 0; i < roundMatches.length && matchesScheduledToday < matchesPerDay; i++) {
        const match = roundMatches[i];
        const hasTeams = match.homeTeamId && match.awayTeamId;

        let scheduled = false;
        let attempts = 0;
        const maxAttempts = venueCount * matchesPerDay;

        while (!scheduled && attempts < maxAttempts) {
          let earliestVenue = venues[0];
          let earliestTime = venueTimings[venues[0]];

          for (const venue of venues) {
            if (venueTimings[venue] < earliestTime) {
              earliestTime = venueTimings[venue];
              earliestVenue = venue;
            }
          }

          const slotTime = new Date(baseStartTime.getTime() + venueTimings[earliestVenue] * interval * 60_000);
          const hasConflict = hasTeams ? hasTeamConflictAtTime(match, slotTime, matchesScheduledThisDate) : false;

          if (!hasConflict) {
            match.startTime = slotTime.toISOString();
            match.venue = earliestVenue;
            match.bestOf = bestOf;

            matchesScheduledThisDate.push(match);
            venueTimings[earliestVenue]++;
            matchIndex++;
            matchesScheduledToday++;
            scheduledCount++;
            scheduled = true;
          } else {
            venueTimings[earliestVenue]++;
            attempts++;
          }
        }

        if (!scheduled) {
          console.warn(
            `Could not schedule match ${match.homeTeamName} vs ${match.awayTeamName} due to team conflicts. Will try next date.`
          );
          break;
        }
      }

      matchesByRound[round] = roundMatches.slice(scheduledCount);
    }

    dateIndex++;
  }
};

// Americano/Mexicano: a round is played simultaneously on separate courts, so every
// match in a round shares one startTime; rounds advance by the configured interval.
// bestOf is always 1 for these formats (single game to a fixed points total).
export const scheduleAmericanoRounds = (params: {
  result: IMatchDetailDTO[];
  configDates: string[];
  matchConfigs: { [key: string]: MatchConfig };
}): void => {
  const { result, configDates, matchConfigs } = params;
  if (configDates.length === 0) return;

  const byRound = new Map<string, IMatchDetailDTO[]>();
  result.forEach((m) => {
    const key = m.round || "Round ?";
    if (!byRound.has(key)) byRound.set(key, []);
    byRound.get(key)!.push(m);
  });
  const numbered = /^Round (\d+)$/;
  const roundKeys = [...byRound.keys()].sort((a, b) => {
    const na = numbered.exec(a);
    const nb = numbered.exec(b);
    if (na && nb) return parseInt(na[1], 10) - parseInt(nb[1], 10);
    return a.localeCompare(b);
  });

  let dateIndex = 0;
  let slotInDay = 0;
  let matchesScheduledToday = 0;

  for (const roundKey of roundKeys) {
    const roundMatches = byRound.get(roundKey)!;
    let config = matchConfigs[configDates[dateIndex]];

    // Move to the next date when this round would exceed the day's match budget
    // (a round is never split across days — it is played simultaneously).
    if (
      matchesScheduledToday > 0 &&
      matchesScheduledToday + roundMatches.length > config.matchesPerDay &&
      dateIndex < configDates.length - 1
    ) {
      dateIndex++;
      slotInDay = 0;
      matchesScheduledToday = 0;
      config = matchConfigs[configDates[dateIndex]];
    }

    // Fill the configured courts in waves: with courts >= matches the whole round is
    // simultaneous; with fewer courts the overflow starts one interval later.
    const courts = Math.max(1, config.venueCount ?? roundMatches.length);
    const roundBase = new Date(config.startTime).getTime() + slotInDay * config.interval * 60_000;
    roundMatches.forEach((m, i) => {
      const wave = Math.floor(i / courts);
      m.startTime = new Date(roundBase + wave * config.interval * 60_000).toISOString();
      m.venue = `Court ${(i % courts) + 1}`;
      m.bestOf = 1;
    });

    slotInDay += Math.ceil(roundMatches.length / courts);
    matchesScheduledToday += roundMatches.length;
  }
};
